import React, { ReactNode, CSSProperties, useRef, useState, useEffect } from "react";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";

import "react-resizable/css/styles.css";

interface DraggableOverlayProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  initialWidth?: number;
  initialHeight?: number;
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  top: 100,
  left: 100,
  zIndex: 9999,
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: 12,
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  overflow: "hidden",
  border: "2px solid rgba(255,255,255,0.1)",
};

const handleStyle: CSSProperties = {
  cursor: "move",
  background: "rgba(255,255,255,0.1)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(255,255,255,0.2)",
  borderRadius: "12px 12px 0 0",
  padding: "12px 20px",
  fontWeight: 600,
  fontSize: "16px",
  color: "#fff",
  userSelect: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export default function DraggableOverlay({ open, onClose, children, title = "Call", initialWidth = 800, initialHeight = 600 }: DraggableOverlayProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: initialWidth, height: initialHeight });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (open) {
      setDimensions({ width: initialWidth, height: initialHeight });
    }
  }, [open, initialWidth, initialHeight]);

  if (!open) return null;

  if (isMobile) {
    const mobileHeightPercent = initialHeight && initialHeight <= 300 ? '30%' : '50%';
    return (
      <>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }
          .overlay-enter {
            animation: fadeIn 0.3s ease-out;
          }
        `}</style>
        <Draggable nodeRef={nodeRef} handle=".handle">
          <div
            ref={nodeRef}
            style={{
              position: 'fixed',
              top: '25%',
              left: '10%',
              width: '80%',
              height: mobileHeightPercent,
              zIndex: 9999,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.1)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            className="overlay-enter"
          >
            <div className="handle" style={{ ...handleStyle, padding: '12px 16px', borderRadius: '12px 12px 0 0' }}>
              <span>{title}</span>
            </div>
            <div style={{ padding: 0, flex: 1, background: "rgba(0,0,0,0.8)", overflow: 'hidden' }}>
              {children}
            </div>
          </div>
        </Draggable>
      </>
    );
  }

  
  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .overlay-enter {
          animation: fadeIn 0.3s ease-out;
        }
        .react-resizable-handle-se {
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff'%3E%3Cpath d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z'/%3E%3C/svg%3E") no-repeat center;
          background-size: 12px;
          background-color: rgba(255,255,255,0.2);
          border-radius: 0 0 8px 0;
          width: 20px !important;
          height: 20px !important;
          bottom: 0;
          right: 0;
        }
        .react-resizable-handle-se:hover {
          background-color: rgba(255,255,255,0.3);
        }
      `}</style>
      <Draggable nodeRef={nodeRef} handle=".handle">
        <div ref={nodeRef} style={{ ...overlayStyle, width: dimensions.width, height: dimensions.height }} className="overlay-enter">
          <ResizableBox
            width={dimensions.width}
            height={dimensions.height}
            minConstraints={[Math.min(600, initialWidth * 0.75), Math.min(400, initialHeight * 0.67)]}
            maxConstraints={[Math.max(1200, initialWidth * 1.5), Math.max(800, initialHeight * 1.33)]}
            onResize={(event, { size }) => setDimensions(size)}
            resizeHandles={['se']}
          >
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div className="handle" style={handleStyle}>
                <span>{title}</span>
              </div>
              <div style={{
                padding: 0,
                flex: 1,
                background: "rgba(0,0,0,0.8)",
                borderRadius: "0 0 12px 12px",
                overflow: "hidden"
              }}>
                {children}
              </div>
            </div>
          </ResizableBox>
        </div>
      </Draggable>
    </>
  );
}
